import { Loading } from "@repo/ui";
import { FC, useMemo } from "react";

import { useOnboardingSessionsApi } from "../../api/onboardingSessionsApi";
import * as Queries from "../../api/queries";
import { useQueryWrapper } from "../../api/repPortalApi";
import { useRepSegmentsApi } from "../../api/repSegmentsApi";
import { OnboardingCompletion } from "../../components/onboardingCompletion";
import { useRepContext } from "../../contexts/editableItemContext";
import { RepSegment } from "../../models";
import { OnboardingRequirements } from "../../models/onboardingRequirement";
import { RepStatusEnum } from "../../models/rep";
import { ReqIcon } from "../onboardingSessions/req";
import { OnboardingTabs } from "./onboardingTabs";
import style from "./style.module.scss";

export const MultiOnboardingRequirementsList: FC = () => {
	const { repId } = useRepContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { loading } = useRepSegmentsQuery();

	return (
		<Loading loading={loading}>
			<div>
				<OnboardingTabs
					renderSegment={(segment) => <OnboardingRequirementsList key={segment.id} segment={segment} />}
				/>
				<hr className={style.horizontalDivider} />
			</div>
		</Loading>
	);
};

interface Props {
	segment: RepSegment;
}

export const OnboardingRequirementsList: FC<Props> = ({ segment }) => {
	const repSegmentId = segment.id;
	const { repId, rep } = useRepContext();

	const { getOnboardingRequirementsBySegment } = useOnboardingSessionsApi();

	const { data: onboardingRequirementsObj, loaded } = useQueryWrapper(
		Queries.ONBOARDING_REQUIREMENTS,
		[repId, repSegmentId],
		() => getOnboardingRequirementsBySegment(repSegmentId),
	);

	const onboardingRequirements = Object.keys(onboardingRequirementsObj ?? {}) as (string &
		keyof OnboardingRequirements)[];

	const onboardingCompletions = onboardingRequirements.map((req) => (
		<OnboardingCompletion req={req} depthToDisplay={0} key={req} repSegmentId={repSegmentId} />
	));

	const completion = useMemo(() => {
		const activeStatuses = [
			RepStatusEnum.Active,
			RepStatusEnum.ContingentStart,
			RepStatusEnum.AwaitingContingentApproval,
		];
		const repActive = activeStatuses.includes(rep.statusEnum);
		const segmentActive = activeStatuses.includes(segment.statusId);
		const fullyActive = segment.statusId === RepStatusEnum.Active && rep.statusEnum == RepStatusEnum.Active;
		return {
			name: "",
			required: false,
			done: fullyActive,
			contingentDone: !fullyActive && repActive && segmentActive,
		};
	}, [rep.statusEnum, segment.statusId]);

	return (
		<Loading loading={!loaded} tag="h5">
			<div id="agent-summary-onboarding" className={style.onboardingRequirements}>
				{!onboardingRequirementsObj?.bypassOnboarding && onboardingCompletions}
				<div className={style.flexWithGap1} key="activeToSell">
					<ReqIcon completion={completion} />
					Activated to Sell
				</div>
			</div>
		</Loading>
	);
};
